import {
  CheckListV2BrakeFluidInfo,
  CheckListV2DrumsInfo,
  CheckListV2InspectedServices,
  CheckListV2InspectedServicesItem,
  CheckListV2PadsInfo,
  CheckListV2RotorsInfo,
  CheckListV2ShoesInfo,
  PreJobCheckListV2,
} from "../../generated/nest-graphql";
import {
  ColorGradeOption,
  getBatteryHealthGrade,
  getConditionColorGrade,
  getFluidLevelColorGrade,
  getFluidPpmColorGrade,
  getPadsThicknessColorGrade,
} from "../PreJobChecklistV2/colorGradeHelpers";
import { BAD_CONDITION_OPTION, GOOD_CONDITION_OPTION } from "../PreJobChecklistV2/constants";
import { formatMiles } from "./formatMiles.utils";

export enum InspectionRowLabel {
  ODOMETER = "Odometer",
  FRONT_PADS = "Front pads",
  REAR_PADS = "Rear pads",
  FRONT_ROTORS = "Front rotors",
  REAR_ROTORS = "Rear rotors",
  REAR_SHOES = "Rear shoes",
  REAR_DRUMS = "Rear drums",
  BRAKE_FLUID = "Brake fluid",
  TIRE_TREAD = "Tire tread",
  BATTERY = "Battery",
  FRONT_LEFT_CALIPER = "Front left caliper",
  FRONT_RIGHT_CALIPER = "Front right caliper",
  REAR_LEFT_CALIPER = "Rear left caliper",
  REAR_RIGHT_CALIPER = "Rear right caliper",
  FRONT_LEFT_HUB_BEARING = "Front left hub bearing",
  FRONT_RIGHT_HUB_BEARING = "Front right hub bearing",
  REAR_LEFT_HUB_BEARING = "Rear left hub bearing",
  REAR_RIGHT_HUB_BEARING = "Rear right hub bearing",
}

const INSPECTED_SERVICES: Array<[keyof Omit<CheckListV2InspectedServices, "__typename">, InspectionRowLabel]> = [
  ["frontLeftCaliper", InspectionRowLabel.FRONT_LEFT_CALIPER],
  ["frontRightCaliper", InspectionRowLabel.FRONT_RIGHT_CALIPER],
  ["rearLeftCaliper", InspectionRowLabel.REAR_LEFT_CALIPER],
  ["rearRightCaliper", InspectionRowLabel.REAR_RIGHT_CALIPER],
  ["frontLeftHubAssemblyBearing", InspectionRowLabel.FRONT_LEFT_HUB_BEARING],
  ["frontRightHubAssemblyBearing", InspectionRowLabel.FRONT_RIGHT_HUB_BEARING],
  ["rearLeftHubAssemblyBearing", InspectionRowLabel.REAR_LEFT_HUB_BEARING],
  ["rearRightHubAssemblyBearing", InspectionRowLabel.REAR_RIGHT_HUB_BEARING],
];

/** Only the parts the technician ticked as inspected. */
const inspectedService = (item?: CheckListV2InspectedServicesItem | null) =>
  item?.included ? joined([item.colorGrade, item.notes]) : null;

/** A severity from the inspection form's own scale and thresholds, so the history matches what the tech saw. */
export type InspectionRow = { label: InspectionRowLabel; value: string | null; grade: ColorGradeOption | null };

const asGrade = (value?: string | null): ColorGradeOption | null =>
  value === "Red" || value === "Yellow" || value === "Green" ? value : null;

/** A row's icon shows its worst reading. */
const worst = (grades: Array<ColorGradeOption | null>): ColorGradeOption | null => {
  if (grades.includes("Red")) return "Red";
  if (grades.includes("Yellow")) return "Yellow";
  return grades.includes("Green") ? "Green" : null;
};

const conditionGrade = (condition?: Array<string | null> | null) => {
  const ticked = (condition ?? []).filter(Boolean) as string[];
  return ticked.length ? getConditionColorGrade(ticked) : null;
};

/** The same values padThickness shows, in the same order of preference, so a hidden stale value cannot set the icon. */
const shownPadThicknesses = (pad?: CheckListV2PadsInfo | null): Array<string | null | undefined> => {
  if (pad?.innerPadThickness || pad?.outerPadThickness) return [pad.innerPadThickness, pad.outerPadThickness];
  if (pad?.driverSide || pad?.passengerSide) return [pad.driverSide, pad.passengerSide];
  return [pad?.padsThickness];
};

const padsGrade = (pad?: CheckListV2PadsInfo | null) =>
  worst([
    ...shownPadThicknesses(pad).map((thickness) => getPadsThicknessColorGrade(thickness ?? "")),
    conditionGrade(pad?.condition),
  ]);

// Rotor thickness is graded against the vehicle's discard thickness, which the history does not load: condition only.
const rotorsGrade = (rotor?: CheckListV2RotorsInfo | null) => conditionGrade(rotor?.condition);

const gradedPartGrade = (part?: CheckListV2ShoesInfo | CheckListV2DrumsInfo | null) =>
  worst([asGrade(part?.colorGrade), conditionGrade(part?.condition)]);

const brakeFluidGrade = (fluid?: CheckListV2BrakeFluidInfo | null) =>
  worst([
    asGrade(fluid?.colorGrade),
    getFluidPpmColorGrade(fluid?.ppm ?? ""),
    getFluidLevelColorGrade(fluid?.level ?? ""),
  ]);

/** The form's wording, shortened for a phone-width row; anything not listed shows as recorded. */
const SHORT_VALUES: Record<string, string> = {
  [GOOD_CONDITION_OPTION]: "Good",
  [BAD_CONDITION_OPTION]: "Bad",
  "Uneven Wear": "Uneven wear",
  "Uneven Wear/Rust lip": "Uneven wear/rust lip",
  "Squeaking/rubbing/scraping noises during road test": "Noisy on road test",
  "Out-of-round drum": "Out of round",
};

const short = (value?: string | null) => (value ? SHORT_VALUES[value.trim()] ?? value.trim() : value);

/** Condition is a list on pads, rotors, shoes and drums — a technician can tick more than one. */
const conditions = (values?: Array<string | null> | null) => (values ?? []).filter(Boolean).map(short).join(", ");

// The form takes these as plain numbers with the unit beside the field; anything older is shown as typed.
const withUnit = (value: string | null | undefined, unit: string) => {
  const text = value?.trim();
  return text && Number.isFinite(Number(text)) ? `${text}${unit}` : short(text);
};

const joined = (parts: Array<string | null | undefined | false>, separator = " · ") =>
  parts.filter(Boolean).join(separator) || null;

/**
 * Each form records wear its own way — inner/outer (core), driver/passenger side (fleet), or one value —
 * so every pair is shown when recorded; `||` because a cleared field is "".
 */
const padThickness = (pad: CheckListV2PadsInfo): string | null => {
  const sides =
    joined(
      [
        pad.innerPadThickness && `inner ${pad.innerPadThickness}`,
        pad.outerPadThickness && `outer ${pad.outerPadThickness}`,
      ],
      ", "
    ) ||
    joined([pad.driverSide && `driver ${pad.driverSide}`, pad.passengerSide && `passenger ${pad.passengerSide}`], ", ");
  if (sides) return `${sides} mm`;
  return pad.padsThickness ? `${pad.padsThickness} mm` : null;
};

const pads = (pad?: CheckListV2PadsInfo | null) =>
  pad ? joined([padThickness(pad), conditions(pad.condition)]) : null;

const rotors = (rotor?: CheckListV2RotorsInfo | null) =>
  rotor ? joined([rotor.thicknessInches && `${rotor.thicknessInches} in`, conditions(rotor.condition)]) : null;

const graded = (part?: CheckListV2ShoesInfo | CheckListV2DrumsInfo | null) =>
  joined([part?.colorGrade, conditions(part?.condition)]);

/** Core forms grade the colour, VIOC records ppm and level (the level alone, e.g. "Full"). */
const brakeFluid = (fluid?: CheckListV2BrakeFluidInfo | null) =>
  joined([fluid?.colorGrade, fluid?.ppm && `${fluid.ppm} ppm`, short(fluid?.level)]);

/**
 * The one list of what an inspection summary shows, so deciding whether a visit has an inspection and
 * rendering it cannot drift apart. Shoes and drums only count when the vehicle uses them, as the form says.
 */
export const inspectionRows = (checklist: PreJobCheckListV2): InspectionRow[] => [
  { label: InspectionRowLabel.ODOMETER, value: formatMiles(checklist.odometer), grade: null },
  { label: InspectionRowLabel.FRONT_PADS, value: pads(checklist.frontPads), grade: padsGrade(checklist.frontPads) },
  { label: InspectionRowLabel.REAR_PADS, value: pads(checklist.rearPads), grade: padsGrade(checklist.rearPads) },
  {
    label: InspectionRowLabel.FRONT_ROTORS,
    value: rotors(checklist.frontRotors),
    grade: rotorsGrade(checklist.frontRotors),
  },
  {
    label: InspectionRowLabel.REAR_ROTORS,
    value: rotors(checklist.rearRotors),
    grade: rotorsGrade(checklist.rearRotors),
  },
  ...(checklist.usesRearShoesAndDrums
    ? [
        {
          label: InspectionRowLabel.REAR_SHOES,
          value: graded(checklist.rearShoes),
          grade: gradedPartGrade(checklist.rearShoes),
        },
        {
          label: InspectionRowLabel.REAR_DRUMS,
          value: graded(checklist.rearDrums),
          grade: gradedPartGrade(checklist.rearDrums),
        },
      ]
    : []),
  {
    label: InspectionRowLabel.BRAKE_FLUID,
    value: brakeFluid(checklist.brakeFluid),
    grade: brakeFluidGrade(checklist.brakeFluid),
  },
  {
    label: InspectionRowLabel.TIRE_TREAD,
    value: joined([
      checklist.tireTreadDepth?.frontDepth && `front ${withUnit(checklist.tireTreadDepth.frontDepth, " in")}`,
      checklist.tireTreadDepth?.rearDepth && `rear ${withUnit(checklist.tireTreadDepth.rearDepth, " in")}`,
    ]),
    // The inspection form has no tread thresholds, so no severity is shown for it.
    grade: null,
  },
  {
    label: InspectionRowLabel.BATTERY,
    value: joined([
      !checklist.withoutCCA && checklist.coldCrankingAmps && `${checklist.coldCrankingAmps} CCA`,
      !checklist.withoutBatteryHealth && withUnit(checklist.batteryHealth, "%"),
    ]),
    // CCA is graded against the vehicle's rated CCA, which the history does not load: health only.
    grade: checklist.withoutBatteryHealth ? null : getBatteryHealthGrade(checklist.batteryHealth ?? ""),
  },
  ...INSPECTED_SERVICES.map(([field, label]) => {
    const item = checklist.inspectedServices?.[field];
    return { label, value: inspectedService(item), grade: item?.included ? asGrade(item.colorGrade) : null };
  }),
];

const AXLE_PART: Array<[RegExp, RegExp, InspectionRowLabel[]]> = [
  [/\bfront\b/, /\bpads?\b/, [InspectionRowLabel.FRONT_PADS]],
  [/\brear\b/, /\bpads?\b/, [InspectionRowLabel.REAR_PADS]],
  [/\bfront\b/, /\brotors?\b/, [InspectionRowLabel.FRONT_ROTORS]],
  [/\brear\b/, /\brotors?\b/, [InspectionRowLabel.REAR_ROTORS]],
  [/\brear\b/, /\bshoes?\b/, [InspectionRowLabel.REAR_SHOES]],
  [/\brear\b/, /\bdrums?\b/, [InspectionRowLabel.REAR_DRUMS]],
  [/\bfront\b/, /\bcalipers?\b/, [InspectionRowLabel.FRONT_LEFT_CALIPER, InspectionRowLabel.FRONT_RIGHT_CALIPER]],
  [/\brear\b/, /\bcalipers?\b/, [InspectionRowLabel.REAR_LEFT_CALIPER, InspectionRowLabel.REAR_RIGHT_CALIPER]],
  [
    /\bfront\b/,
    /\b(hub|bearing)s?\b/,
    [InspectionRowLabel.FRONT_LEFT_HUB_BEARING, InspectionRowLabel.FRONT_RIGHT_HUB_BEARING],
  ],
  [
    /\brear\b/,
    /\b(hub|bearing)s?\b/,
    [InspectionRowLabel.REAR_LEFT_HUB_BEARING, InspectionRowLabel.REAR_RIGHT_HUB_BEARING],
  ],
];

const AXLE = /\b(front|rear)\b/g;
const PART = /\b(pads?|rotors?|shoes?|drums?|calipers?|hubs?|bearings?)\b/;
const SEGMENTS = /\s*(?:\band\b|&|,|\+|\/)\s*/;

// Each part takes its own axle ("Front Pads and Rear Rotors"), a shared one ("Front and Rear Pads"), or the last
// one named ("Front Pads & Rotors"); a name with no axle marks nothing rather than guessing.
export const servicedInspectionLabels = (serviceNames: string[]): Set<InspectionRowLabel> => {
  const serviced = new Set<InspectionRowLabel>();
  serviceNames.forEach((raw) => {
    const name = raw.toLowerCase();
    const mark = (text: string) =>
      AXLE_PART.forEach(([axle, part, labels]) => {
        if (axle.test(text) && part.test(text)) labels.forEach((label) => serviced.add(label));
      });
    let pendingAxles = "";
    let lastAxles = "";
    // Parts named before any axle ("Pads and Rotors, Front") take the first axle that follows.
    let axleless: string[] = [];
    name.split(SEGMENTS).forEach((segment) => {
      const axles = (segment.match(AXLE) ?? []).join(" ");
      if (!PART.test(segment)) {
        pendingAxles = `${pendingAxles} ${axles}`.trim();
        return;
      }
      const text = `${pendingAxles || (axles ? "" : lastAxles)} ${segment}`;
      lastAxles = (text.match(AXLE) ?? []).join(" ");
      pendingAxles = "";
      if (!lastAxles) {
        axleless.push(segment);
        return;
      }
      axleless.forEach((part) => mark(`${lastAxles} ${part}`));
      axleless = [];
      mark(text);
    });
    if (pendingAxles) axleless.forEach((part) => mark(`${pendingAxles} ${part}`));
    if (/\bbrake fluid\b/.test(name)) serviced.add(InspectionRowLabel.BRAKE_FLUID);
    if (/\bbattery\b/.test(name)) serviced.add(InspectionRowLabel.BATTERY);
  });
  return serviced;
};

/** Each inspection item with the services that worked on it, by the same name rule. */
export const servicesByInspectionItem = <T extends { name: string }>(services: T[]): Map<InspectionRowLabel, T[]> => {
  const byItem = new Map<InspectionRowLabel, T[]>();
  services.forEach((service) =>
    servicedInspectionLabels([service.name]).forEach((label) =>
      byItem.set(label, [...(byItem.get(label) ?? []), service])
    )
  );
  return byItem;
};
